import { useContext, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import history from '@history';
import { useDownloadBlob, useNativeDebounce, useRequest } from '@main/hooks';
import WdPageSimple from '@main/core/WdPageSimple';
import { styled } from '@mui/material/styles';
import { CircularProgress, Grid, Switch, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {
  BasicSelect,
  ButtonWithExpand,
  ComposedHeader,
  ComposedTable,
  InputSearch,
  Paginator,
} from '@main/components';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import Button from '@mui/material/Button';
import SystemSecurityUpdateOutlinedIcon from '@mui/icons-material/SystemSecurityUpdateOutlined';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useSimpleAlert } from '@main/hooks/useSimpleAlert';
import { formatDate } from 'src/app/utils/dates';
import {
  catalogCompanies,
  catalogLocations,
  deleteKiosk,
  exportFile,
  searchKiosks,
  statusKiosk,
} from 'src/app/api/kiosk';
import { useExtractSelector } from '@main/hooks/useExtractSelector';
import { showMessage } from 'app/store/core/messageSlice';
import _ from 'lodash';
import { typeDownloadEnum } from 'src/app/enums/fileEnum';
import { defaultsEnum } from 'src/app/enums/defaultsEnum';
import { useTheme } from '@mui/styles';
import { customSwitchStyles } from 'app/styles/styled/customSwitchStyles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const Root = styled(WdPageSimple)(({ theme }) => ({
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const exportFileOptions = _.omit(typeDownloadEnum, ['txt', 'zip']);

const statusLicensesData = [
  {
    label: 'Todo',
    value: '',
  },
  {
    label: 'Activo',
    value: 'true',
  },
  {
    label: 'Inactivo',
    value: 'false',
  },
];

const tableRowScheme = [
  {
    title: 'Nombre',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Clave',
    minWidth: '320px',
    width: '320px',
  },
  {
    title: 'Descripción',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Estatus',
    minWidth: '80px',
  },
  {
    title: 'ID Dispositivo',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Modelo',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Hostname',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Zona',
    minWidth: '180px',
    maxWidth: '200px',
  },
  {
    title: 'Edificio',
    minWidth: '180px',
    maxWidth: '200px',
  },
  {
    title: 'Locación',
    minWidth: '180px',
    maxWidth: '200px',
  },
  {
    title: 'Compañía',
    minWidth: '180px',
    maxWidth: '200px',
  },
  {
    title: 'Actualización',
    minWidth: '120px',
  },
  {
    title: 'Creación',
    minWidth: '120px',
  },
  {
    title: 'Opciones',
    minWidth: '165px',
    fixedRight: true,
  },
];

const OptionButtons = memo(({ onUpdate, onDelete }) => {
  return (
    <div className="flex w-full flex-wrap flex-row justify-between items-center">
      <Tooltip title="Actualizar informacion de registro de terminal">
        <span>
          <Button
            className="my-3"
            onClick={onUpdate}
            size="small"
            variant="contained"
            color="secondary"
            style={{ width: '90px', justifyContent: 'center' }}
            startIcon={<EditOutlinedIcon />}
          >
            Editar
          </Button>
        </span>
      </Tooltip>

      <Tooltip title="Eliminar informacion de registro de terminal">
        <span>
          <Button
            className="my-3"
            onClick={onDelete}
            size="small"
            variant="contained"
            color="error"
            sx={{ minWidth: '3.2rem', aspectRatio: 1 / 1, padding: '0rem' }}
          >
            <DeleteForeverIcon />
          </Button>
        </span>
      </Tooltip>
    </div>
  );
});

const Terminals = () => {
  const Dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const openAlert = useSimpleAlert();

  // ------ States correspondiente a componentes de filtrado -------
  const [dataList, setDataList] = useState([]);
  const [querySearch, setQuerySearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(25);
  const [datePickerValue, setDatePickerValue] = useState([null, null]);
  const [form, setForm] = useState({ query: '', initDate: '', endDate: '', paginate: undefined });
  const theme = useTheme();
  // ------- Selects --------------
  const [statusSelect, setStatusSelect] = useState('');
  const [locationSelect, setLocationSelect] = useState('');
  const [companySelect, setCompanySelect] = useState('');

  // ----- Customs States ----------

  // ----- Instancia de request que se usaran en la vista junto a sus valores de estado ---------
  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const {
    makeRequest: tryGetData,
    isLoading,
    response,
  } = useRequest(searchKiosks, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });
  const { makeRequest: tryDeleteKiosk } = useRequest(deleteKiosk);
  const { makeRequest: tryEnableTerminal } = useRequest(statusKiosk);

  const { dataSelect: companyDataSelect, dataIndex: companyIndex } = useExtractSelector(
    catalogCompanies,
    {
      emptyOption: true,
      label: 'label',
      value: 'value',
    }
  );
  const { dataSelect: locationDataSelect, makeRequest: locationTryRequest } = useExtractSelector(
    catalogLocations,
    {
      autoFetch: false,
      emptyOption: true,
      label: 'label',
      value: 'value',
    }
  );

  const { triggerAction } = useNativeDebounce({
    delay: 600,
  });

  const {
    formats,
    tryDownload,
    progress: progressExport,
    isLoading: isLoadingExport,
  } = useDownloadBlob(exportFileOptions, exportFile);

  const handleExportFile = async (format) => {
    await tryDownload({
      type: format,
      name: 'Kioscos',
    });
  };

  // ---- Borra todos los filtros ------
  const handleClearAllFilters = () => {
    setQuerySearch('');
    setCurrentPage(1); // Establecer a la primera página
    setCurrentItemsPerPage(25);
    setDatePickerValue([null, null]);
    // _ _ _ Limpeza de ComboBox
    setStatusSelect('');
    setCompanySelect('');
    setLocationSelect('');
  };

  const handleRequestTerminals = () => {
    triggerAction().then((isOk) => {
      if (!isOk) return;
      tryGetData(form, currentPage);
    });
  };

  // ----- Actualiza el formulario acorde cambio de filtros
  useEffect(() => {
    if (
      querySearch === undefined ||
      currentItemsPerPage === undefined ||
      datePickerValue === undefined
    )
      return;
    setForm((current) => ({
      ...current,
      query: querySearch,
      paginate: currentItemsPerPage,
      initDate: datePickerValue[0] === null ? '' : datePickerValue[0],
      endDate: datePickerValue[1] === null ? '' : datePickerValue[1],
      status: statusSelect === '' ? null : statusSelect.toString(),
      location_key: locationSelect === '' ? null : locationSelect.toString(),
      company_key: companySelect === '' ? null : companySelect.toString(),
    }));
  }, [
    querySearch,
    currentItemsPerPage,
    datePickerValue,
    statusSelect,
    locationSelect,
    companySelect,
  ]);

  // ------ Actualiza la información de la tabla después de la petición -------
  useEffect(() => {
    if (!response) return;
    setDataList(response.data);
    setTotalPages(response.last_page);
    setTotalItems(response.total);
    setCurrentPage(response.current_page);
  }, [response]);

  // -------- Realiza la petición de datos al haber un cambio en el formulario -----
  useEffect(() => {
    if (form === undefined || form.paginate === undefined) return;
    console.log('FROM | QUERY => ', form);
    handleRequestTerminals();
  }, [currentPage, form]);

  // ------ Inicialización de la vista con toda la información disponible -------
  useEffect(() => {
    return () => {
      setDataList([]);
    };
  }, []);

  // ------------ Actualizacion anidada de Selects -------------
  useEffect(() => {
    setLocationSelect('');
    if (companySelect === '') return;
    locationTryRequest({
      company_key: companySelect,
    });
  }, [companySelect]);

  // ---------- Actualizacion de tabla acorde socket -----------
  // useEffect(() => {
  //   if (companySelect.trim() === '' || companySelect === undefined) {
  //     setReactiveTableScheme(tableRowScheme);
  //   } else {
  //     setReactiveTableScheme([columnSocketState, ...tableRowScheme]);
  //   }
  // }, [companySelect]);

  // ----------- Actualizacion de end points de Socket ---------
  // useEffect(() => {
  //   if (companyIndex === undefined || companySelect.trim() === '' || companySelect === undefined) {
  //     turnOffSocket();
  //   } else {
  //     const selectedUri = getObjFromArrayByKey(companyIndex, 'key', companySelect);
  //     const { socket, socketPort } = selectedUri;
  //     const newUrlSocket = `${socket || 'localhost'}:${socketPort || '3000'}`;
  //     console.log('SELECTED URL', newUrlSocket);
  //     startSocket(newUrlSocket);
  //   }
  // }, [companyIndex, companySelect]);

  // ------ Funciones para actualizar los filtros ------
  const handleSearchQuery = (query = '') => {
    if (!query.length > 3) {
      return;
    }
    setQuerySearch(query);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangeItems = (items) => {
    setCurrentItemsPerPage(items);
    setCurrentPage(1);
  };

  // ----- Acciones de botones en la tabla ----------
  const handleCreate = () => {
    history.push('/kiosks/create');
  };

  const handleUpdate = (id) => {
    history.push(`/kiosks/update?id=${id}`);
  };

  const handleEnable = (terminalId, terminalStatus) => {
    console.log(terminalId, terminalStatus);
    const builtForm = {
      status: !terminalStatus,
    };
    openAlert({
      title: 'Atención',
      description: terminalStatus
        ? `¿Está seguro que desea deshabilitar la terminal?`
        : `¿Está seguro que desea habilitar la terminal?`,
    }).then((userResponse) => {
      if (!userResponse) return;
      console.log(builtForm);
      tryEnableTerminal(terminalId, builtForm).then(({ isOk }) => {
        if (isOk) {
          Dispatch(
            showMessage({
              message: terminalStatus ? `Terminal deshabilitada` : `Terminal habilitada`,
              autoHideDuration: 4000,
              variant: 'success',
            })
          );
          tryGetData(form);
        }
      });
    });
  };

  const handleDelete = (id) => {
    openAlert({
      title: 'Atención',
      description: '¿Está seguro que desea eliminar la terminal?',
    }).then((userResponse) => {
      if (!userResponse) {
        return;
      }
      tryDeleteKiosk(id)
        .then(({ isOk }) => {
          if (isOk) {
            Dispatch(
              showMessage({
                message: 'Terminal de reconocimiento eliminada correctamente',
                autoHideDuration: 3000,
              })
            );
            tryGetData(form);
          }
        })
        .catch((error) => console.log(error));
    });
  };

  const handleInfo = (id) => {
    history.push(`/kiosks/info?id=${id}`);
  };

  return (
    <Root
      header={
        <ComposedHeader title="Terminales de Kioskos" description="Análisis de datos">
          <div className="px-4 py-4">
            <Button
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="primary"
              onClick={handleCreate}
              startIcon={<SystemSecurityUpdateOutlinedIcon />}
            >
              Agregar terminal
            </Button>
          </div>
          <div className="px-4 py-4">
            <ButtonWithExpand
              title={!isLoadingExport ? 'Descargar' : `Descargando ${progressExport}%`}
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="success"
              startIcon={
                isLoadingExport ? (
                  <CircularProgress
                    color="inherit"
                    size="1.8rem"
                    variant={progressExport > 1 ? 'determinate' : 'indeterminate'}
                    value={progressExport}
                  />
                ) : (
                  <SimCardDownloadOutlinedIcon />
                )
              }
            >
              {formats.map((option, index) => (
                <ButtonWithExpand.Item
                  disabled={isLoadingExport}
                  key={`${index}_${option.value}_doc`}
                  text={`Documento ${option.label}`}
                  onClick={() => handleExportFile(option.value)}
                />
              ))}
            </ButtonWithExpand>
          </div>
        </ComposedHeader>
      }
      content={
        <Grid container className="px-32 pb-32 pt-0 flex flex-row w-full justify-center">
          <Grid item xs={12} md={12} className="flex w-full">
            <Card className="pb-24 px-24 mt-16 w-full">
              <CardContent className="flex w-full h-full flex-col justify-between">
                <div className="flex flex-col mt-24">
                  <Grid container className="w-full flex justify-center items-center py-8 mb-28">
                    <Grid item xs={12} md={7}>
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="ml-4 mr-8 my-2 w-8/12 md:my-0">
                          <InputSearch
                            value={querySearch}
                            onChange={(query) => handleSearchQuery(query)}
                          />
                        </div>
                        <div className="ml-4 my-2 md:my-0">
                          <Button
                            fullWidth
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={handleClearAllFilters}
                          >
                            Limpiar filtros
                          </Button>
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <div className="flex flex-row justify-center md:justify-end items-center md:mt-0 mt-8">
                        <DateRangePicker
                          slotProps={{ textField: { size: 'small' } }}
                          localeText={{ start: 'Fecha inicial', end: 'Fecha final' }}
                          value={datePickerValue}
                          onChange={(current) => setDatePickerValue(current)}
                          disableFuture
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} className="p-2">
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="px-4">
                          <BasicSelect
                            data={companyDataSelect}
                            onChange={(data) => setCompanySelect(data)}
                            title="Compañía"
                            value={companySelect}
                            size="small"
                          />
                        </div>
                        <div className="px-4">
                          <BasicSelect
                            data={locationDataSelect}
                            onChange={(data) => setLocationSelect(data)}
                            title="Locación"
                            value={locationSelect}
                            size="small"
                            disabled={companySelect === ''}
                          />
                        </div>
                        <div className="px-4">
                          <BasicSelect
                            data={statusLicensesData}
                            onChange={(data) => setStatusSelect(data)}
                            title="Estatus"
                            value={statusSelect}
                            size="small"
                          />
                        </div>
                        {/*
                          Agrega mas selects
                        */}
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row justify-end">
                        <Paginator
                          totalPages={totalPages}
                          totalItems={totalItems}
                          onChangeCurrentPage={(page) => handleChangePage(page)}
                          onChangeItemsPerPage={(items) => handleChangeItems(items)}
                          currentPage={currentPage}
                          currentItemsPerPage={currentItemsPerPage}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <div className="flex flex-col justify-center">
                    <ComposedTable
                      isLoading={isLoading}
                      className="w-full"
                      headers={tableRowScheme}
                      data={dataList}
                    >
                      <ComposedTable.Column
                        content={({ id, name }) => (
                          <Tooltip title={id}>
                            <Button
                              size="small"
                              className="font-normal transition-colors duration-100 ease-in-out hover:text-blue-500"
                              onClick={() => handleInfo(id)}
                            >
                              {name}
                            </Button>
                          </Tooltip>
                        )}
                      />
                      <ComposedTable.Column content={({ key }) => key} />
                      <ComposedTable.Column content={({ description }) => description} />
                      <ComposedTable.Column
                        content={({ id, status }) =>
                          id ? (
                            <Switch
                              checked={status}
                              inputProps={{
                                'aria-label': 'Estatus',
                              }}
                              sx={customSwitchStyles(theme)}
                              onChange={() => handleEnable(id, status)}
                            />
                          ) : (
                            defaultsEnum.labels.table
                          )
                        }
                      />
                      <ComposedTable.Column content={({ deviceId }) => deviceId || 'No asociado'} />
                      <ComposedTable.Column content={({ model }) => model || 'No asociado'} />
                      <ComposedTable.Column content={({ hostname }) => hostname || 'No asociado'} />
                      <ComposedTable.Column
                        content={({ zoneName, zone_key: zoneKey }) =>
                          zoneName || zoneKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column
                        content={({ buildingName, building_key: buildingKey }) =>
                          buildingName || buildingKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column
                        content={({ locationName, location_key: locationKey }) =>
                          locationName || locationKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column
                        content={({ companyName, company_key: companyKey }) =>
                          companyName || companyKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column content={(item) => formatDate(item.updated_at)} />
                      <ComposedTable.Column content={(item) => formatDate(item.created_at)} />
                      <ComposedTable.Column
                        fixedRight
                        content={({ id }) => (
                          <OptionButtons
                            onUpdate={() => handleUpdate(id)}
                            onDelete={() => handleDelete(id)}
                          />
                        )}
                      />
                    </ComposedTable>
                  </div>
                </div>
                <Grid container spacing={1} className="p-2 mt-24 flex">
                  <Grid item xs={12} md={12} className="py-8">
                    <div className="flex flex-row justify-end">
                      <Paginator
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onChangeCurrentPage={(page) => handleChangePage(page)}
                        onChangeItemsPerPage={(items) => handleChangeItems(items)}
                        currentPage={currentPage}
                        currentItemsPerPage={currentItemsPerPage}
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      }
    />
  );
};

export default Terminals;

